import React, { useEffect, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { default as DefaultInput } from '@/components/Input';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next'; // Import the useTranslation hook
import {
  CountriesResponse,
  Country,
  State,
  getCountries,
} from '@/services/CountryService';
import { Branch, getBranch, saveBranch } from '@/services/BranchService';
import Spinner from '@/components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import Dropdown from '@/components/DropDown';
import { getCompanyId } from '@/services/AuthService';
import { toast } from 'react-hot-toast';
import FormButtons from '@/components/shared/FormButtons';

const validationSchema = Yup.object().shape({
  title: Yup.string().required('BranchForm.titleRequired'),
});

interface FormValues extends Branch {
  title: string;
}

function BranchForm() {
  const { id } = useParams();
  const editForm = id !== 'new';
  const navigate = useNavigate();

  const { t } = useTranslation(); // Initialize the useTranslation hook

  const [formikValuesState, setFormikValuesState] = useState<FormValues>({
    title: '',
    id: '',
  });
  const [loading, setLoading] = useState(false);

  const {
    data: branchData,
    isLoading,
    refetch: refetchBranch,
  } = useQuery<Branch | undefined>(
    ['branch', id],
    async () => await getBranch(id!),
    {
      enabled: editForm,
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      onSuccess: (branch) => {
        if (branch) {
          setFormikValuesState((prev) => ({ ...prev, ...branch }));
        }
      },
    }
  );

  useEffect(() => {
    if (editForm && branchData) {
      // Update form values with branch data
      setFormikValuesState((prev) => ({ ...prev, ...branchData }));
    }
  }, [editForm, branchData]);

  const handleSaveBranch = async (
    values: FormValues,
    { resetForm, validateForm }: any
  ) => {
    setLoading(true);

    try {
      var companyId = getCompanyId();
      const branch: Branch = {
        ...values,
        companyId: companyId!,
      };

      const data = await saveBranch(branch);
      if (data) {
        if (editForm) {
          toast.success(
            t('SuccessUpdateMessage', {
              name: t('ToastMsg.Branch'),
            })
          );
        } else {
          toast.success(
            t('SuccessMessage', {
              name: t('ToastMsg.Branch'),
            })
          );
        }
      }
      if (data && editForm) {
        refetchBranch();
      }
      navigate(-1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={
          editForm ? t('BranchForm.editBranch') : t('BranchForm.addBranch')
        }
      />
      {loading ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={formikValuesState}
          validationSchema={validationSchema}
          onSubmit={handleSaveBranch}
          enableReinitialize={editForm}
        >
          {({ values, errors, handleChange, handleSubmit, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <div className="group relative z-0 mb-6 w-full">
                <DefaultInput
                  type="text"
                  name="title"
                  id="title"
                  placeholder=" "
                  onChange={handleChange}
                  errors={errors}
                  value={values.title}
                  label={t('BranchForm.titleLabel')}
                />
              </div>

              <FormButtons resetForm={resetForm} loading={loading} />
            </form>
          )}
        </Formik>
      )}
    </DefaultLayout>
  );
}

export default BranchForm;
